import { useState, useEffect } from 'react';
import api from '../services/api';
import Toast from './Toast';

interface ShareModalProps {
  listId: string;
  listName: string;
  onClose: () => void;
}

export default function ShareModal({ listId, listName, onClose }: ShareModalProps) {
  const [inviteLink, setInviteLink] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' | 'warning' } | null>(null);

  // Automatisch Invite-Link erstellen beim Öffnen
  useEffect(() => {
    generateInvite();
  }, [listId]);

  const generateInvite = async () => {
    setLoading(true);
    try {
      const response = await api.post('/invitations', {
        listId,
        role: 'MEMBER',
      });
      // Backend sendet bereits den kompletten inviteLink mit der richtigen Domain
      const link = response.data.data.inviteLink;
      setInviteLink(link);
      setToast({ message: 'Einladungslink erstellt', type: 'success' });
    } catch (error) {
      console.error('Error generating invite:', error);
      setToast({ message: 'Fehler beim Erstellen des Einladungslinks', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      setCopied(true);
      setToast({ message: 'Link in Zwischenablage kopiert!', type: 'success' });
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      // Fallback für ältere Browser
      const textArea = document.createElement('textarea');
      textArea.value = inviteLink;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setToast({ message: 'Link in Zwischenablage kopiert!', type: 'success' });
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '1rem',
      }}
      onClick={onClose}
    >
      <div
        className="card"
        style={{ maxWidth: '500px', width: '100%' }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem' }}>
          "{listName}" teilen
        </h2>

        <p style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem' }}>
          Erstelle einen Einladungslink, den du mit anderen teilen kannst.
        </p>

        {!inviteLink ? (
          <button
            onClick={generateInvite}
            className="btn btn-primary"
            style={{ width: '100%', marginBottom: '1rem' }}
            disabled={loading}
          >
            {loading ? 'Erstelle Link...' : '🔗 Einladungslink erstellen'}
          </button>
        ) : (
          <div>
            <div
              className="code-box"
              style={{
                marginBottom: '1rem',
              }}
            >
              {inviteLink}
            </div>

            <button
              onClick={copyToClipboard}
              className="btn btn-primary"
              style={{ width: '100%', marginBottom: '1rem' }}
            >
              {copied ? '✓ Kopiert!' : '📋 Link kopieren'}
            </button>

            <div
              style={{
                padding: '1rem',
                backgroundColor: 'var(--bg-tertiary)',
                borderRadius: '0.5rem',
                fontSize: '0.875rem',
                color: 'var(--text-secondary)',
                border: '1px solid var(--border-color)'
              }}
            >
              <p style={{ marginBottom: '0.5rem' }}>ℹ️ <strong>So funktioniert's:</strong></p>
              <ul style={{ paddingLeft: '1.5rem' }}>
                <li>Teile diesen Link per WhatsApp, E-Mail, etc.</li>
                <li>Personen ohne Konto werden zur Registrierung geleitet</li>
                <li>Nach der Anmeldung haben sie Zugriff auf diese Liste</li>
                <li>Der Link läuft nach 7 Tagen ab</li>
              </ul>
            </div>
          </div>
        )}

        <button
          onClick={onClose}
          className="btn btn-secondary"
          style={{ width: '100%', marginTop: '1rem' }}
        >
          Schließen
        </button>
      </div>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}
